import {
  ClusterOutlined,
  SafetyOutlined,
  TeamOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { CheckPermissionType, OAuthPermission } from "@thepiquelab/web-types";
import React, { lazy, useMemo } from "react";
import { IsPoweredByQianKun } from "../config";
import { useAccessControl } from "../hooks/useAccessControl";

export interface RoutesProps {
  path: string;
  label: string;
  hide?: boolean;
  icon?: React.ForwardRefExoticComponent<any>;
  hideItems?: boolean;
  items?: Omit<RoutesProps, "items">[];
  element?: React.LazyExoticComponent<React.FC>;
  permissions?: OAuthPermission[];
  permissionCheckType?: CheckPermissionType;
}

export const ExcludeRouters = ["/error"];

const useRouter = () => {
  const { hasSomePermissions } = useAccessControl();

  const routes: RoutesProps[] = [
    {
      path: "/",
      label: "System Users",
      icon: UserOutlined,
      hide: !hasSomePermissions([OAuthPermission.SYSTEM_USER_VIEW]),
      permissions: [OAuthPermission.SYSTEM_USER_VIEW],
      hideItems: true,
      items: [
        {
          path: "",
          label: "List",
          permissions: [OAuthPermission.SYSTEM_USER_VIEW],
          hide: true,
          element: lazy(() => import("../pages/Users/ListUsers")),
        },
        {
          path: `profile/:adminId`,
          label: "Create User",
          permissions: [OAuthPermission.SYSTEM_USER_VIEW],
          hide: true,
          element: lazy(() => import("../pages/Users/UserProfile/UserProfile")),
        },
        {
          path: "create",
          label: "Create User",
          permissions: [OAuthPermission.SYSTEM_USER_UPDATE],
          hide: true,
          element: lazy(() => import("../pages/Users/CreateUser/CreateUser")),
        },
        {
          path: "edit/:adminId",
          label: "Edit User",
          permissions: [OAuthPermission.SYSTEM_USER_UPDATE],
          hide: true,
          element: lazy(() => import("../pages/Users/EditUser/EditUser")),
        },
      ],
    },
    {
      path: "/role",
      label: "Roles",
      hide: !hasSomePermissions([OAuthPermission.ROLE_VIEW]),
      icon: ClusterOutlined,
      hideItems: true,
      permissions: [OAuthPermission.ROLE_VIEW],
      items: [
        {
          path: "",
          label: "Role Details",
          permissions: [OAuthPermission.ROLE_VIEW],
          hide: true,
          element: lazy(() => import("../pages/Roles/ListRoles")),
        },
        {
          path: `details/:id`,
          label: "Role Details",
          permissions: [OAuthPermission.ROLE_VIEW],
          hide: true,
          element: lazy(() => import("../pages/Roles/RoleDetails/RoleDetails")),
        },
        {
          path: `details/:id/add-permissions`,
          label: "Role Details",
          permissions: [OAuthPermission.ROLE_UPDATE],
          hide: true,
          element: lazy(
            () =>
              import(
                "../pages/Roles/RoleDetails/AddPermissions/AddPermissions"
              ),
          ),
        },
      ],
    },
    {
      path: "/group",
      label: "Groups",
      hide: !hasSomePermissions([OAuthPermission.GROUP_VIEW]),
      icon: TeamOutlined,
      hideItems: true,
      permissions: [OAuthPermission.GROUP_VIEW],
      items: [
        {
          path: "",
          label: "Groups",
          permissions: [OAuthPermission.GROUP_VIEW],
          hide: true,
          element: lazy(() => import("../pages/Groups/ListGroups")),
        },
        {
          path: `details/:id`,
          label: "Group Details",
          permissions: [OAuthPermission.GROUP_VIEW],
          hide: true,
          element: lazy(
            () => import("../pages/Groups/GroupDetails/GroupDetails"),
          ),
        },
      ],
    },
    {
      path: "/permissions",
      label: "Permissions",
      icon: SafetyOutlined,
      hide: !hasSomePermissions([OAuthPermission.PERMISSION_VIEW]),
      permissions: [OAuthPermission.PERMISSION_VIEW],
      element: lazy(() => import("../pages/Permissions/ListPermissions")),
    },
    {
      path: "/error",
      label: "Error",
      hide: true,
      element: lazy(
        () => import("../pages/Errors/InsufficientCredentialsPage"),
      ),
    },
  ];

  const authorizedRoutes = useMemo(() => {
    return routes
      .filter((r) => !r.permissions || hasSomePermissions(r.permissions))
      .map((r) => {
        return {
          ...r,
          items: r.items?.filter((i) =>
            hasSomePermissions(i.permissions || []),
          ),
        };
      });
  }, [routes]);

  const redirectPath = useMemo(() => {
    if (IsPoweredByQianKun) {
      return "/user";
    }

    return (
      routes.find((r) => hasSomePermissions(r.permissions || []))?.path ??
      "/error"
    );
  }, [routes, IsPoweredByQianKun]);

  return { routes: authorizedRoutes, redirectPath };
};

export default useRouter;
